import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/EditOutlined";
import { httpClient } from "api/httpClient";
import { useSimpleModalToggle } from "components/dialogs/useSimpleModal";
import React from "react";
import { FormWithRedirect, useNotify, useRefresh, useTranslate } from "react-admin";
import { useNotifyError } from "utils/notifiers/useNotifyError";

import {
	EditDocumentTemplateBodyFile,
	EditDocumentTemplateBodyInfo,
} from "./AddEditDocumentTemplateBody";
import { TemplateInfoPreviewButton, TemplateInfoPreviewDialog } from "./TemplateInfoPreviewButton";
import { ValidationDetailsPreviewDialog } from "./ValidationDetailsPreviewDialog";

export const UpdateDocumentTemplate = ({
	document_template,
	is_file_update,
	enumObj = null,
	companyId = null,
}) => {
	const [isLoading, setIsLoading] = React.useState(false);
	const [isInfoDialogOpen, setIsInfoDialogOpen] = React.useState(false);
	const [isValidationDetailsOpen, setIsValidationDetailsOpen] = React.useState(false);
	const [validationDetails, setValidationDetails] = React.useState({});

	const refresh = useRefresh();
	const { open, handleOpen, handleClose } = useSimpleModalToggle();
	const notify = useNotify();
	const notifyError = useNotifyError();
	const translate = useTranslate();

	const handleSubmit = (values) => {
		setIsLoading(true);
		const data = new FormData();
		if (is_file_update) {
			data.append("file", values.file.rawFile);
			if (companyId) {
				data.append("company_id", companyId);
			}
		} else {
			data.append("name", values.name);
			data.append("type", values.type);
		}

		httpClient(`document-templates/${document_template.id}`, { method: "PATCH", body: data })
			.then((res) => {
				handleClose();
				refresh();
				notify(translate("osfc.dialogs.document-templates.update.success"), "success");
			})
			.catch((error) => {
				if (error.body?.body?.details) {
					setValidationDetails(error.body.body.details);
					validationDetailsCloseDialogToggle();
				}
				notifyError(error);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const infoDialogToggle = () => {
		setIsInfoDialogOpen((prev) => !prev);
	};

	const validationDetailsCloseDialogToggle = () => {
		setIsValidationDetailsOpen((prev) => {
			if (prev) {
				setValidationDetails({});
			}
			return !prev;
		});
	};

	return (
		<>
			{is_file_update ? (
				<Button size="small" variant="contained" color="primary" onClick={handleOpen}>
					{translate("resources.document-template.button.change_file")}
				</Button>
			) : (
				<IconButton aria-label="edit" size="small" color="default" onClick={handleOpen}>
					<EditIcon color="primary" />
				</IconButton>
			)}
			{open && (
				<Dialog open={true} onClose={handleClose} maxWidth="md">
					<DialogTitle>{translate("resources.document-template.edit.title")}</DialogTitle>
					<FormWithRedirect
						record={document_template}
						submitOnEnter={false}
						component={DialogContent}
						save={handleSubmit}
						render={({ handleSubmitWithRedirect, form, record }) => (
							<React.Fragment>
								{is_file_update ? (
									<EditDocumentTemplateBodyFile form={form} record={record} />
								) : (
									<EditDocumentTemplateBodyInfo
										form={form}
										record={record}
										isDefaultDocTemplate={!document_template.company_id ? true : false}
										enumObj={enumObj}
									/>
								)}
								<DialogActions>
									{is_file_update && document_template?.type && (
										<TemplateInfoPreviewButton
											translate={translate}
											onInfoButtonClick={infoDialogToggle}
										/>
									)}
									<Button disabled={isLoading} onClick={handleClose} color="primary">
										{translate("ra.action.cancel")}
									</Button>
									<Button disabled={isLoading} onClick={handleSubmitWithRedirect} color="primary">
										{translate("ra.action.update")}
									</Button>
								</DialogActions>
							</React.Fragment>
						)}
					/>
				</Dialog>
			)}
			{open && isInfoDialogOpen && !isValidationDetailsOpen && (
				<TemplateInfoPreviewDialog
					translate={translate}
					documentTemplateType={document_template?.type}
					onInfoDialogClose={infoDialogToggle}
					companyId={companyId}
				/>
			)}
			{open && !isInfoDialogOpen && isValidationDetailsOpen && (
				<ValidationDetailsPreviewDialog
					translate={translate}
					onValidationDetailsDialogClose={validationDetailsCloseDialogToggle}
					validationDetails={validationDetails}
				/>
			)}
		</>
	);
};
